import { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { navigate } from "wouter/use-browser-location";
import { useParams } from "wouter";
import PatientInfoComponent from "../components/patients/PatientInfoComponent";
import SummaryOfPatient from "../components/patients/SummaryOfPatient";
import AppointmentModal from "../components/appointments/AppointmentModal";
import { useQuery } from "@tanstack/react-query";
import { fetchSummaryofSummaries } from "../api/summaryOfSummaries";
import { PageNavigation } from "../components/ui/page-navigation";
import { format, isToday } from "date-fns";
import { fetchCallHistory } from "../api/callHistory";
import { formatUsDate } from "../lib/dateUtils";
import { usePermission } from "../hooks/use-permission";
import { fetchPatientsDetails } from "../redux/patient-actions";

const EMPTY_ARRAY = [];

const PatientReports = () => {
  const { patientId } = useParams();

  const dispatch = useDispatch();
  const patients = useSelector((state) => state.patients.patients) || EMPTY_ARRAY;
  const appointments = useSelector((state) => state.appointments.appointments) || EMPTY_ARRAY;
  
  const loggedInDoctor = useSelector((state) => state.me.me);
  const doctorEmail = loggedInDoctor?.email?.toLowerCase();

  const [hasFetched, setHasFetched] = useState(false);

  const patient = useMemo(
    () => patients.find((p) => String(p.patient_id) === String(patientId)),
    [patients, patientId]
  );

  useEffect(() => {
    if (!patient && loggedInDoctor && !hasFetched) {
      if (loggedInDoctor.clinicName) {
        dispatch(fetchPatientsDetails(loggedInDoctor.clinicName));
      } else {
        dispatch(fetchPatientsDetails());
      }
      setHasFetched(true);
    }
  }, [dispatch, patient, loggedInDoctor, hasFetched]);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [summaryOfSummariesData, setSummaryOfSummariesData] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const canViewPatientInfo = usePermission("patients.info", "read");
  const canViewClinicalSummary = usePermission("patients.clinical_summary", "read");
  const canViewUpcomingAppointment = usePermission("patients.upcoming_appointment", "read");
  const canViewPreviousCalls = usePermission("patients.previous_calls", "read");
  const canJoinCallPermission = usePermission("patients.join_call", "write");
  const canViewPostCall = usePermission("patients.post_call_doc", "read");
  const { data: summaryData } = useQuery({
    queryKey: ["summaryOfSummaries", patientId],
    queryFn: () => fetchSummaryofSummaries(patientId),
    enabled: !!patientId,
  });

  useEffect(() => {
    if (summaryData) setSummaryOfSummariesData(summaryData);
  }, [summaryData]);

  useEffect(() => {
    if (doctorEmail) {
      fetchCallHistory([doctorEmail])
        .then((res) => setCallHistory(res || []))
        .catch(() => setCallHistory([]));
    }
  }, [doctorEmail]);

  const getUnifiedDate = (appt) =>
    appt.appointment_date ||
    appt.date ||
    appt.timestamp ||
    appt.created_at ||
    null;

  const sortedAppointments = useMemo(() => {
    return appointments
      .filter((a) => String(a.patient_id) === String(patientId))
      .sort(
        (a, b) =>
          new Date(getUnifiedDate(b)) - new Date(getUnifiedDate(a))
      );
  }, [appointments, patientId]);

  const mergedAppointments = useMemo(() => {
    return sortedAppointments.map((appt) => {
      const match = callHistory.find(
        (h) => h.appointmentID === appt.id
      );

      const apptDateStr = getUnifiedDate(appt);
      const apptDateObj = apptDateStr
        ? new Date(apptDateStr + "T12:00:00")
        : null;

      const startTimeObj = match?.startTime
        ? new Date(match.startTime)
        : null;
      const endTimeObj = match?.endTime
        ? new Date(match.endTime)
        : null;

      let durationMinutes = null;
      if (startTimeObj && endTimeObj) {
        const diff = Math.round((endTimeObj - startTimeObj) / 60000);
        if (!Number.isNaN(diff) && diff >= 0) durationMinutes = diff;
      }

      const isCompleted = !!match?.endTime;

      return {
        appt,
        history: match || null,
        apptDateObj,
        durationMinutes,
        isCompleted,
      };
    });
  }, [sortedAppointments, callHistory]);

  const now = useMemo(() => new Date(), []);

  const nextUpcoming = useMemo(() => {
    return (
      mergedAppointments
        .map((m) => {
          const dateStr = m.appt.appointment_date;
          if (!dateStr) return null;

          const timeStr = m.appt.time || "00:00";
          const dt = new Date(`${dateStr}T${timeStr}:00`);

          const status = (m.appt.status || "").toLowerCase();
          const cancelled = status === "cancelled" || status === "canceled";

          if (isNaN(dt.getTime())) return null;
          if (cancelled || m.isCompleted) return null;
          if (dt <= now) return null;

          return { meta: m, dt };
        })
        .filter(Boolean)
        .sort((a, b) => a.dt - b.dt)[0] || null
    );
  }, [mergedAppointments, now]);

  const canJoin = useMemo(() => {
    if (!nextUpcoming) return false;

    const { meta, dt } = nextUpcoming;
    const status = (meta.appt.status || "").toLowerCase();
    const cancelled = status === "cancelled" || status === "canceled";

    return (
      canJoinCallPermission &&
      isToday(dt) &&
      dt > now &&
      !cancelled &&
      !meta.isCompleted
    );
  }, [canJoinCallPermission, nextUpcoming, now]);

  const firstName =
    patient?.firstname ||
    patient?.first_name ||
    patient?.details?.firstname ||
    patient?.details?.first_name ||
    "";

  const middleName =
    patient?.middlename ||
    patient?.middle_name ||
    patient?.details?.middlename ||
    patient?.details?.middle_name ||
    patient?.original_json?.details?.middlename ||
    patient?.original_json?.original_json?.details?.middlename ||
    "";

  const lastName =
    patient?.lastname ||
    patient?.last_name ||
    patient?.details?.lastname ||
    patient?.details?.last_name ||
    "";

  const fullName = `${firstName}${middleName ? " " + middleName : ""}${
    lastName ? " " + lastName : ""
  }`;

  // FIX: correct phone mapping + masked fallback
  const rawPhone =
    patient?.phone ||
    patient?.contactmobilephone ||
    patient?.details?.contactmobilephone ||
    "";

  const maskedPhone = rawPhone
    ? `XXX-XXX-${String(rawPhone).slice(-4)}`
    : "******";

  const maskedEmail = patient?.email
    ? `${patient.email[0]}***@${patient.email.split("@")[1]}`
    : "Not Available";

  const insuranceProvider = patient?.insurance_provider || "N/A";
  const insuranceId = patient?.insurance_id || "N/A";

  const lastVisit = useMemo(() => {
    const today = new Date();

    const completed = mergedAppointments
      .filter((m) => m.isCompleted && m.apptDateObj)
      .sort((a, b) => b.apptDateObj - a.apptDateObj);

    if (completed.length > 0) {
      return format(completed[0].apptDateObj, "MMM dd, yyyy");
    }

    const hasTodayAppt = mergedAppointments.some(
      (m) =>
        m.apptDateObj &&
        m.apptDateObj.toDateString() === today.toDateString()
    );

    if (hasTodayAppt) {
      return format(today, "MMM dd, yyyy");
    }

    return "Not Available";
  }, [mergedAppointments]);

  const rawDOB =
    patient?.dob ||
    patient?.date_of_birth ||
    patient?.birthDate ||
    patient?.details?.dob ||
    patient?.original_json?.details?.dob ||
    patient?.original_json?.original_json?.details?.dob;

  const formattedDOB = formatUsDate(rawDOB);
  const clinicName =
    patient?.clinicName?.trim() || loggedInDoctor?.clinicName?.trim() || "";
  const showPatientOverviewSection =
    canViewPatientInfo || canViewClinicalSummary;

  const firstDocumentedAppointmentIndex = mergedAppointments.findIndex(
    (m) => m.isCompleted && canViewPostCall
  );

  return (
    <div className="p-6 w-full space-y-6">
      <PageNavigation
        title="Patient Reports"
        subtitle={fullName}
        customTrail={[
          { href: "/patients", label: "Patients" },
          { href: `/patients/${patientId}`, label: "Patient Details" },
          { href: `/patients/${patientId}/reports`, label: "Reports", isLast: true },
        ]}
      />

      {showPatientOverviewSection ? (
        <div
          className="bg-white border rounded-xl shadow p-6 space-y-6"
          data-tour="patient-overview"
        >
          {canViewPatientInfo ? (
            <PatientInfoComponent
              firstName={firstName}
              middleName={middleName}
              lastName={lastName}
              phone={maskedPhone}
              email={maskedEmail}
              insuranceProvider={insuranceProvider}
              insuranceId={insuranceId}
              lastVisit={lastVisit}
              totalAppointments={mergedAppointments.length}
              dob={formattedDOB}
              clinicName={clinicName}
            />
          ) : null}

          {canViewClinicalSummary ? (
            <SummaryOfPatient summaryDataProp={summaryOfSummariesData} />
          ) : null}
        </div>
      ) : null}

      {canViewUpcomingAppointment ? (
        <div
          className="bg-white border rounded-xl shadow p-6 space-y-4"
          data-tour="patient-upcoming"
        >
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Upcoming Appointment</h3>
          </div>

        {nextUpcoming ? (
          <div className="text-sm text-gray-800 space-y-1">
            <p>
              <strong>Date:</strong>{" "}
              {format(nextUpcoming.dt, "MMM dd, yyyy")}
            </p>
            <p>
              <strong>Time:</strong>{" "}
              {nextUpcoming.meta.appt.time ?? "N/A"}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span className="px-2 py-1 bg-gray-100 border rounded text-xs">
                {nextUpcoming.meta.appt.status}
              </span>
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No upcoming appointments scheduled.</p>
        )}

        {canJoin && nextUpcoming ? (
            <button
              type="button"
              data-tour="patient-join-call"
              onClick={() => setSelectedAppointment(nextUpcoming.meta.appt)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              Join Call
            </button>
          ) : (
            <p data-tour="patient-join-call" className="text-sm text-gray-500">
              Join Call appears here when a same-day visit is ready to start.
            </p>
          )}
      </div>
      ) : null}

      <AppointmentModal
        selectedAppointment={selectedAppointment}
        setSelectedAppointment={setSelectedAppointment}
      />

      {canViewPreviousCalls ? (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Appointment History</h3>
        {mergedAppointments.length === 0 ? (
          <p className="text-sm text-gray-500 rounded-xl border bg-white p-5 shadow">
            No appointment history for this patient yet.
          </p>
        ) : (
          <div
            className="appointment-history-list space-y-3 rounded-xl bg-slate-600/25 p-3"
            data-tour="patient-appointment-history"
          >
            {mergedAppointments.map((m, index) => {
              const {
                appt,
                history,
                apptDateObj,
                isCompleted,
                durationMinutes,
              } = m;

              const dateLabel =
                apptDateObj && !isNaN(apptDateObj.getTime())
                  ? format(apptDateObj, "MMM dd, yyyy")
                  : "N/A";

              return (
                <div
                  key={appt.id}
                  className="appointment-history-card bg-white rounded-xl px-4 py-3 flex justify-between items-center"
                >
                  <div>
                    <p className="font-semibold text-gray-900">
                      {dateLabel} at {appt.time ?? "N/A"}
                    </p>

                    <p className="text-sm text-gray-500 mt-0.5">
                      Doctor:{" "}
                      {appt.doctor_name ||
                        appt.doctor_email?.split("@")[0]}
                    </p>
                    {isCompleted && (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                        Completed
                      </span>
                    )}
                    {durationMinutes != null && (
                      <p className="text-xs text-gray-500 mt-1">
                        Duration: {durationMinutes} min
                      </p>
                    )}
                  </div>

                  {isCompleted && canViewPostCall && (
                    <button
                      type="button"
                      title="View Documentation"
                      data-tour={
                        index === firstDocumentedAppointmentIndex
                          ? "patient-visit-documentation"
                          : undefined
                      }
                      onClick={() => {
                        const username =
                          history?.userID ||
                          appt.doctor_email ||
                          doctorEmail;

                        const route = username
                          ? `/post-call/${appt.id}?username=${encodeURIComponent(
                              username
                            )}`
                          : `/post-call/${appt.id}`;

                        navigate(route);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      ) : null}
    </div>
  );
};

export default PatientReports
